import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { Pressable, StyleSheet, Text, View, type TextProps, type ViewProps } from 'react-native'
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
  type SharedValue
} from 'react-native-reanimated'

import { cn } from '@/utils/cn'

interface ModalState {
  openModal: (config: ModalConfig) => void
  closeModal: () => void
  isModalOpen: boolean
}

const ModalContext = createContext<ModalState>({
  openModal: () => undefined,
  closeModal: () => undefined,
  isModalOpen: false
})

export function ModalProvider({ children }: { children: ReactNode }) {
  const [modalConfig, setModalConfig] = useState<ModalConfig | null>(null)
  const opacity = useSharedValue(0)
  const openModal = useCallback((config: ModalConfig) => setModalConfig(config), [])
  const closeModal = useCallback(() => {
    opacity.value = withTiming(0, { duration: 100 }, () => {
      runOnJS(setModalConfig)(null)
    })
  }, [])
  const isModalOpen = modalConfig !== null

  return (
    <ModalContext.Provider value={{ openModal, closeModal, isModalOpen }}>
      {children}
      {modalConfig && <Modal {...modalConfig} opacity={opacity} />}
    </ModalContext.Provider>
  )
}

export function useModal() {
  return useContext(ModalContext)
}

type ModalConfig = {
  className?: string
  children: ReactNode
  closeOnBackdropClick?: boolean
  onClose: () => void
}

function Modal({
  className,
  children,
  closeOnBackdropClick = false,
  onClose,
  opacity
}: ModalConfig & { opacity: SharedValue<number> }) {
  const viewStyle = useAnimatedStyle(() => {
    return { opacity: opacity.value }
  })

  useEffect(() => {
    opacity.value = withTiming(1, { duration: 200 })
  }, [])
  // NOTE: `className` is not mapped to styles on Reanimated's animated
  // components, so overlay positioning is expressed with `style` here.
  return (
    <Animated.View style={[styles.overlay, viewStyle]}>
      <View className={'flex-1 items-center justify-center'}>
        <Pressable className={'absolute size-full bg-black/30'} onPress={closeOnBackdropClick ? onClose : undefined} />
        <View className={cn('w-[360px] rounded-xl bg-background-primary shadow-3', className)}>{children}</View>
      </View>
    </Animated.View>
  )
}

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    width: '100%',
    height: '100%'
  }
})

export function ModalHeader({ children, className }: ViewProps) {
  return <View className={cn('h-14 items-center p-2', className)}>{children}</View>
}

export function ModalTitle({ children, className }: TextProps) {
  return <Text className={cn('text-base font-medium text-text-primary', className)}>{children}</Text>
}

export function ModalBody({ children, className }: ViewProps) {
  return <View className={cn('items-center px-4 py-2', className)}>{children}</View>
}

export function ModalFooter({ children, className }: ViewProps) {
  return <View className={cn('flex-row items-center gap-x-2 p-4', className)}>{children}</View>
}
